import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('crm_token');
    const savedUser = localStorage.getItem('crm_user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setIsAuthenticated(true);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (e) {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password, tenantSlug = null, rememberMe = false) => {
    try {
      // Send credentials as JSON body (not query params) for security
      const payload = { email, password, remember_me: !!rememberMe };
      if (tenantSlug) {
        payload.tenant_slug = tenantSlug;
      }
      
      const response = await api.post('/auth/login', payload);
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('crm_token', access_token);
      localStorage.setItem('crm_user', JSON.stringify(userData));
      sessionStorage.setItem('crm_show_welcome', '1');
      
      // Store tenant info if present
      if (userData.tenant_id) {
        localStorage.setItem('crm_tenant_id', userData.tenant_id);
      }
      
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setUser(userData);
      setIsAuthenticated(true);
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    localStorage.removeItem('crm_tenant_id');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  /**
   * Admin-only: swap the current session token for one that authenticates as
   * another user (impersonation). Saves the original admin session under
   * `crm_admin_session` so we can restore it via `stopImpersonating()`.
   */
  const startImpersonating = (accessToken, userData) => {
    // Preserve current admin session so we can switch back later.
    const adminToken = localStorage.getItem('crm_token');
    const adminUser = localStorage.getItem('crm_user');
    if (adminToken && adminUser && !localStorage.getItem('crm_admin_session')) {
      localStorage.setItem('crm_admin_session', JSON.stringify({ token: adminToken, user: adminUser }));
    }
    localStorage.setItem('crm_token', accessToken);
    localStorage.setItem('crm_user', JSON.stringify(userData));
    if (userData.tenant_id) localStorage.setItem('crm_tenant_id', userData.tenant_id);
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setUser(userData);
    setIsAuthenticated(true);
  };

  const stopImpersonating = () => {
    const saved = localStorage.getItem('crm_admin_session');
    if (!saved) return false;
    try {
      const { token, user: u } = JSON.parse(saved);
      localStorage.setItem('crm_token', token);
      localStorage.setItem('crm_user', u);
      localStorage.removeItem('crm_admin_session');
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const userObj = JSON.parse(u);
      setUser(userObj);
      setIsAuthenticated(true);
      if (userObj.tenant_id) localStorage.setItem('crm_tenant_id', userObj.tenant_id);
      return true;
    } catch (e) {
      return false;
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    startImpersonating,
    stopImpersonating,
    isImpersonating: !!localStorage.getItem('crm_admin_session'),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
